import { printTools, scrollToId } from "@/lib/scroll";
import { DownloadChoices } from "@/components/DownloadChoices";
import { TopBar } from "@/components/TopBar";
import { Footer } from "@/components/Footer";
import { PackPage, packIndex } from "@/components/workbook/PackPage";
import { SimpleTool, Info } from "@/components/workbook/SimpleTool";
import { T3, TRACKS, VERSION_LOG } from "@/data/toolkit3";

const toolEl = (t) => {
  const El = ({ n }) => (
    <SimpleTool id={t.id} storageKey={`${t.id}-v1`} tablesFirst tool={{ n, title: t.title, blurb: TRACKS[t.track].name }}
      intro={<Info items={[["Operational purpose", t.purpose], ...(t.kpis ? [["Target metrics & KPIs", t.kpis]] : []), ...(t.actions ? [["Governance action requirements", t.actions]] : [])]} />}
      tables={[{ name: "rows", title: t.table.title, cols: t.table.cols, seed: t.table.seed, rows: 2, grid: "sm:grid-cols-2 lg:grid-cols-3", addLabel: "Add a row" }]}
      groups={[{ title: "Notes and next steps", cols: "", fields: [{ k: "notes", label: "Notes", rows: 3 }] }]} />
  );
  return El;
};

const Purpose = () => (
  <section id="t3-purpose" aria-labelledby="t3-purpose-title" className="tool-section scroll-mt-36 rounded-[1.75rem] border-2 border-line bg-paper p-5 sm:p-8 lg:p-10">
    <h3 id="t3-purpose-title" className="font-display text-2xl font-medium sm:text-3xl">Executive purpose</h3>
    <p className="mt-4 max-w-3xl leading-relaxed">This framework sets an operational baseline for SEND governance, resource management and teaching practice. It aims to move from reactive SEND support to a data-informed, legally secure approach that narrows attainment gaps, safeguards funding and maximises pupil progress.</p>
    <p className="mt-4 max-w-3xl leading-relaxed">Keep the framework text as it is so results can be compared across departments and partner schools. Add your local data, Local Offer contacts and branding, and record any local changes in the Local Improvements and Versions Log at the end.</p>
    <ul className="mt-5 grid gap-3 md:grid-cols-3">
      {TRACKS.map((t) => <li key={t.id} className="rounded-2xl border-2 border-line bg-surface p-4"><p className="eyebrow">{t.colour} track</p><p className="mt-1 font-bold">{t.name.split(": ")[1]}</p></li>)}
    </ul>
    <p className="mt-5 text-sm text-ink-soft">Pupil and family details are replaced with reference codes. Never enter names.</p>
  </section>
);

const VersionLog = ({ n }) => (
  <SimpleTool id="t3-versions" storageKey="t3-versions-v1" tool={{ n, title: VERSION_LOG.title, blurb: "Record every local adjustment without overwriting the framework text." }} metaFields={false}
    tables={[{ name: "rows", title: "Versions", cols: VERSION_LOG.cols, seed: VERSION_LOG.seed, grid: "sm:grid-cols-2 lg:grid-cols-5" }]} />
);

const ORDER = [...T3].sort((a, b) => a.track - b.track);
const SECTIONS = [
  { id: "t3-purpose", n: "", title: "Executive purpose", El: Purpose },
  ...ORDER.map((t) => ({ id: t.id, n: t.n, title: t.title, El: toolEl(t) })),
  { id: "t3-versions", n: "", title: "Versions log", El: VersionLog },
];
const T3_INDEX = packIndex("Framework", SECTIONS);

export default function Toolkit3Page() {
  return (
    <>
      <TopBar minimal index={T3_INDEX} />
      <main id="main" tabIndex={-1} className="outline-none">
        <section className="no-print mx-auto max-w-[1400px] px-5 pb-14 pt-[150px] sm:px-8 lg:px-10 lg:pt-[178px]" aria-labelledby="t3-title">
          <p className="eyebrow mb-4">Toolkit 3 · Free · No login</p>
          <h1 id="t3-title" className="max-w-4xl font-display text-[2.6rem] font-medium leading-[1] tracking-[-0.03em] sm:text-6xl">SEND Master <span className="italic text-brand">Operational Framework</span></h1>
          <p className="mt-6 max-w-2xl text-lg text-ink-soft">Ten operational toolkits across three tracks: governance and finance, teaching and interventions, and data, integration and people. Fill them in here, then print or save as PDF.</p>
          <DownloadChoices className="mt-8" file="send-master-operational-framework.pdf" resource="toolkit-3" label="the framework (PDF)" testid="t3-download"
            onPrint={() => printTools(null)} onFillOnline={() => scrollToId("toolkit-3")} />
        </section>
        <PackPage packId="toolkit-3" label="framework" sections={SECTIONS}
          cover={{ eyebrow: "TOOLKIT 3", title: "SEND Master Operational Framework", subtitle: "Policy directive and ten operational toolkits", lines: TRACKS.map((t) => `${t.colour}: ${t.name.split(": ")[1]}`) }} />
      </main>
      <Footer />
    </>
  );
}
